import React, { useCallback } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { IRecordCompositeScoreInput } from '../../Score/ManageEvaluations/manage-evaluations.interface';
import { useLatestRef } from '../../../lib/hooks/use-latest-ref';
import { videoSrc } from '../../../lib/utils/video.utils';
import { CALIBRATION_ROUND } from './my-assignments.constants';
import { isGroupScored, makeVideoSkillKey } from './assignment-scoring.utils';
import { ICalibrationClipEntry } from './assignment-scoring.types';
import { CalibrationSkillRow } from './calibration-skill-row.component';

interface ICalibrationAssignmentItemProps {
  entry: ICalibrationClipEntry;
  compositeScoreHash: { [videoSkillKey: string]: IRecordCompositeScoreInput };
  pendingScoreKeys?: ReadonlySet<string>;
  recordCompositeScore: (op: IRecordCompositeScoreInput) => void;
  removeCompositeScore: (op: IRecordCompositeScoreInput) => void;
  onScored?: () => void;
  onVideoUnavailable?: (filename: string) => void;
}

// One clip shown once with every skill to score beneath it. onScored fires only when the whole
// clip is scored, so the pager waits for all skills on the page before advancing.
export const CalibrationAssignmentItem: React.FC<ICalibrationAssignmentItemProps> = ({
  entry,
  compositeScoreHash,
  pendingScoreKeys,
  recordCompositeScore,
  removeCompositeScore,
  onScored,
  onVideoUnavailable,
}) => {
  const { assignment, group } = entry;
  const { drill, calibrationVideo, items } = group;

  // The HEAD probe keeps indeterminate clips (405/501/5xx/network); a real playback failure is the
  // last seam to exclude an unplayable clip so the evaluator never scores a blank video.
  const handleVideoError = useCallback(() => {
    if (calibrationVideo?.filename) onVideoUnavailable?.(calibrationVideo.filename);
  }, [calibrationVideo, onVideoUnavailable]);

  // calibrationAssignmentId rides on additionalInfo so the server links the composite to this
  // item; no assessmentSessionId — calibration is session-less.
  const buildOp = useCallback((item: ICalibrationAssignmentItem, raw: string, type: string, clientId?: string): IRecordCompositeScoreInput => ({
    calibrationVideoId: item.calibrationVideoId,
    drillId: item.drillId,
    skillId: item.skillId,
    compositeIndex: null,
    round: CALIBRATION_ROUND,
    raw,
    type,
    clientId,
    additionalInfo: { calibrationAssignmentId: assignment._id },
  }), [assignment._id]);

  // Handlers read the hash through a latest-value ref so their identities survive the
  // per-keystroke hash updates — that stability is what lets the rows' React.memo skip.
  const hashRef = useLatestRef(compositeScoreHash);

  // advance is opt-in: SCORE/TIMED save once per deliberate action; the number field saves per
  // keystroke (advance false). Advance the pager only when every skill on this clip is scored.
  const handleSave = useCallback((item: ICalibrationAssignmentItem, raw: string, type: string, advance = false) => {
    const hash = hashRef.current;
    const op = buildOp(item, raw, type, hash[makeVideoSkillKey(item)]?.clientId);
    recordCompositeScore(op);
    if (!advance) return;
    // The hash (last render's) hasn't picked up `op` yet, so patch it in locally rather
    // than waiting a render to re-check isGroupScored.
    if (isGroupScored(group, { ...hash, [makeVideoSkillKey(item)]: op })) onScored?.();
  }, [buildOp, hashRef, recordCompositeScore, group, onScored]);

  const handleRemove = useCallback((item: ICalibrationAssignmentItem) => {
    const buffered = hashRef.current[makeVideoSkillKey(item)];
    removeCompositeScore(buildOp(item, buffered?.raw ?? '', buffered?.type ?? '', buffered?.clientId));
  }, [buildOp, hashRef, removeCompositeScore]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 16,
        borderBottom: '1px solid #ddd',
      }}
    >
      {!!drill?.name && (
        <Typography variant="subtitle1" align="center" gutterBottom>{drill.name}</Typography>
      )}
      {!!calibrationVideo?.filename && (
        <Box my={1}>
          {/* onError on both: a failed <source> fetch fires on the source (no bubble), a decode/
              unsupported failure fires on the <video>. handleVideoError is idempotent. */}
          <video controls loop muted playsInline preload="auto" autoPlay style={{ maxWidth: '100%' }} onError={handleVideoError}>
            <source src={`${videoSrc}/${calibrationVideo.filename}`} type="video/mp4" onError={handleVideoError} />
          </video>
        </Box>
      )}
      {items.map((item) => (
        <CalibrationSkillRow
          key={makeVideoSkillKey(item)}
          item={item}
          buffered={compositeScoreHash[makeVideoSkillKey(item)]}
          pending={pendingScoreKeys?.has(makeVideoSkillKey(item)) === true}
          onSave={handleSave}
          onRemove={handleRemove}
        />
      ))}
    </div>
  );
};
